// A visit's usage totals (engagement, route computations, tiles, map moves, actions), for the personas
// and the admin: counted in memory, sent as DELTAS in one beacon every FLUSH_EVERY while the page is
// visible and once when it is hidden — never a request per action. The server sums them per visit
// (Api::V1::TrackingController::USAGE_COUNTERS), so a lost beacon only loses its own slice.
import {hasBotCharacteristics} from '@js/modules/botDetection';

const ENDPOINT = '/api/v1/tracking/usage';
const FLUSH_EVERY = 5 * 60 * 1000;
// Active time: the page is visible and the visitor did something in the last IDLE_AFTER.
const ACTIVE_TICK = 5000;
const IDLE_AFTER = 30000;

let counts = {};
let scrollPct = 0;
let sentScrollPct = 0;
let firstActionMs = null;
let firstActionSent = false;
let lastInputAt = -Infinity;
let started = false;
let automated = null;
let scrollPending = false;

const isAutomated = () => {
    automated ??= hasBotCharacteristics();
    return automated;
};

const isDirty = () => Object.keys(counts).length > 0 || scrollPct > sentScrollPct || (firstActionMs !== null && !firstActionSent);

const flush = () => {
    if (!isDirty() || typeof navigator.sendBeacon !== 'function') {
        return;
    }

    const body = {page: window.perfPage || '', counts};
    if (scrollPct > sentScrollPct) {
        body.scroll_pct = scrollPct;
    }
    if (firstActionMs !== null && !firstActionSent) {
        body.first_action_ms = firstActionMs;
    }

    counts = {};
    sentScrollPct = scrollPct;
    firstActionSent = firstActionMs !== null;

    navigator.sendBeacon(ENDPOINT, new Blob([JSON.stringify(body)], {type: 'application/json'}));
};

const onInput = () => {
    lastInputAt = performance.now();
};

const measureScroll = () => {
    scrollPending = false;
    const height = document.documentElement.scrollHeight;
    if (height > 0) {
        scrollPct = Math.max(scrollPct, Math.min(100, Math.round(100 * (window.scrollY + window.innerHeight) / height)));
    }
};

const onScroll = () => {
    onInput();
    if (!scrollPending) {
        scrollPending = true;
        requestAnimationFrame(measureScroll);
    }
};

const UsageTotals = {
    // key: one of USAGE_COUNTERS (or x_<conversion>); unknown keys are dropped by the server.
    add(key, count = 1) {
        if (!started || !Number.isFinite(count) || count <= 0) {
            return;
        }

        counts[key] = (counts[key] || 0) + Math.round(count);
    },

    // The first real action of the page (search, drawing, geolocation...), from the navigation.
    firstAction() {
        if (started && firstActionMs === null) {
            firstActionMs = Math.round(performance.now());
        }
    },

    initialize() {
        // An admin's visit is not tracked by the server (tracking_active?): nothing to send.
        if (started || window.currentAdminId || isAutomated()) {
            return;
        }

        started = true;
        ['pointerdown', 'keydown', 'wheel', 'touchstart'].forEach((type) => window.addEventListener(type, onInput, {passive: true, capture: true}));
        window.addEventListener('scroll', onScroll, {passive: true});
        window.addEventListener('beforeprint', () => UsageTotals.add('prints'));

        setInterval(() => {
            if (document.visibilityState === 'visible' && performance.now() - lastInputAt <= IDLE_AFTER) {
                UsageTotals.add('active_ms', ACTIVE_TICK);
            }
        }, ACTIVE_TICK);
        setInterval(() => {
            if (document.visibilityState === 'visible') {
                flush();
            }
        }, FLUSH_EVERY);

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                flush();
            }
        });
        window.addEventListener('pagehide', flush);
    }
};

export default UsageTotals;
